import { ListingHero } from 'app/components/listing-hero';
import { Perishable } from 'app/design/icons/perishable';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Calendar } from 'app/design/primitives/calendar';
import { PCImage } from 'app/design/primitives/image';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Modal } from 'app/design/primitives/modal';
import { Radio } from 'app/design/primitives/radio';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import { isDate } from 'app/lib/date';
import { isPublicServiceCategory } from 'app/network/category';
import { isQuickOffer, useOneListingPossibleLenders } from 'app/network/listing';
import { useCreateOneLoan } from 'app/network/loan';
import { useUser } from 'app/network/user';
import {
  LoanFeeFormFields,
  LoanFeeModals,
  LoanFeeModalsState,
  useLoanFeeModals,
} from 'app/screens/management/loan-fee';
import { CategorySection } from 'app/screens/new/components/category-section';
import { DateSection } from 'app/screens/new/components/date-section';
import { DuplicatesStepper } from 'app/screens/new/components/duplicates-stepper';
import { ExpirableAvailabilitySection } from 'app/screens/new/components/expirable-availability-section';
import { GroupsSection } from 'app/screens/new/components/groups-section';
import { PublicServiceRequestSection } from 'app/screens/new/components/public-service-request-section';
import { UrgentSection } from 'app/screens/new/components/urgent-section';
import { useCalendar } from 'app/screens/shared/loan-dates-picker';
import { AvailabilitySection, InventoryField } from 'app/screens/shared/one-listing/components/availability-section';
import { ListingContext } from 'app/screens/shared/one-listing/components/ListingProvider';
import { ViewsSection } from 'app/screens/shared/one-listing/components/views-section';
import TravelDistanceSection from 'app/screens/shared/travel-distance';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import React, { useCallback, useContext, useEffect, useEffectEvent, useMemo, useState, type RefObject } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { FormProvider } from 'react-hook-form';

import { IconTools, IconX } from '@tabler/icons-react-native';
import { RemoteError } from 'app/design/primitives/remote-error';
import { ResourceTypeButton } from 'app/screens/new/components/haute-gaspesie-offer-form';
import { TransactionModeToggle } from 'app/screens/new/components/transaction-mode-toggle';
import { useAutomaticMessage } from 'app/screens/new/use-automatic-message';
import { BonificationBadge } from 'app/screens/shared/one-listing/components/bonification-badge';
import { createTheme as createDSTheme } from 'app/shopify';

import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

enum NeedFoundModalSteps {
  FoundOrDelete = 1,
  FoundPCOrElsewhere = 2,
  FoundElsewhere = 3,
  SelectLender = 4,
  SelectDates = 5,
  Done = 6,
}

export interface EditListingActions {
  remove: () => void;
  save: () => void;
}

export const EditListingForm: React.FC<Listing & { actionsRef?: RefObject<EditListingActions | null> }> = ({
  actionsRef,
  ...listing
}) => {
  const {
    onImageChange,
    onSharePress,
    form,
    t,
    onSubmit,
    routeParams,
    setShowActions,
    updateListing,
    isFormInvalid,
    setIsFormInvalid,
  } = useContext(ListingContext);
  const navigation = useNavigation();
  const deviceHeight = useDeviceHeight();

  const isRequest = listing.type === ListingType.request;
  const isOffer = !isRequest;
  const isService = listing.subtype === ListingSubtype.service;
  const transactionMode = form.watch('transactionMode');
  const isDonation = isDonationListing({
    isDonation: listing.isDonation,
    subtype: listing.subtype,
    transactionMode,
  });
  const isHauteGaspesieOffer = isOffer && isWhiteLabel(WhiteLabel.HauteGaspesie);
  const { data: user } = useUser();
  const transactionModeEnabled = useMemo(
    () => isFeatureEnabled(FeatureFlag.transactionMode, user?.featureFlags),
    [user?.featureFlags],
  );
  const showRequestTransactionMode =
    isRequest &&
    isWhiteLabel(WhiteLabel.HauteGaspesie) &&
    (listing.subtype === ListingSubtype.object || listing.subtype === ListingSubtype.expirable) &&
    transactionModeEnabled;

  const isPublicServiceRequest = isPublicServiceCategory(listing.serviceCategory?.id);

  const [isNeedFoundModalOpen, setIsNeedFoundModalOpen] = useState(false);
  const [needFoundModalCurrentStep, setNeedFoundModalCurrentStep] = useState(NeedFoundModalSteps.FoundOrDelete);
  const [needFoundModalNextStep, setNeedFoundModalNextStep] = useState(NeedFoundModalSteps.FoundOrDelete);
  const [selectedLender, setSelectedLender] = useState('');
  const { selectedStartDate, selectedEndDate, onDatePress } = useCalendar(listing, undefined, isService || isDonation);

  const listingUsersQuery = useOneListingPossibleLenders(listing.id, {
    enabled: needFoundModalCurrentStep >= NeedFoundModalSteps.FoundPCOrElsewhere,
  });

  const loanFeeAvailable = useMemo(
    () => isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags),
    [user?.featureFlags],
  );

  const { loanFeeEnabled, setLoanFeeEnabled, loanFeeModalsState } = useEditListingLoanFee(listing);

  const { control, register, setValue } = form;
  const subtype = form.watch('subtype');
  const isExpirable = subtype === ListingSubtype.expirable;
  const perishableEnabled = useMemo(
    () => isFeatureEnabled(FeatureFlag.perishableListings, user?.featureFlags),
    [user?.featureFlags],
  );
  const { assistantMessageDefault, onChangeAutomaticMessage } = useAutomaticMessage({
    updateForm: isRequest ? form : undefined,
    persistedMessage: isRequest ? (listing.automaticMessage ?? null) : undefined,
  });

  const saveChanges = useCallback(() => {
    if (!loanFeeEnabled) {
      setValue('loanFee', null);
      setValue('loanFeeDescription', null);
      setValue('loanFeePaymentMethod', null);
    }
    setIsFormInvalid(false);
    void onSubmit();
  }, [loanFeeEnabled, onSubmit, setIsFormInvalid, setValue]);

  const { setResourceType, setTransactionMode, transactionOptions, requestTransactionOptions } =
    useEditListingTransactionMode({
      isRequest,
      isExpirable,
      isHauteGaspesieOffer,
      showRequestTransactionMode,
      transactionMode,
      setLoanFeeEnabled,
      setValue,
      t,
    });

  const { mutate: createLoan } = useCreateOneLoan();

  // newServiceCategoryId is sent back from service-categories screen, when picking a new category
  useEffect(() => {
    if (!routeParams.newServiceCategoryId) return;
    setValue('serviceCategoryId', routeParams.newServiceCategoryId);
    register('serviceCategoryId');
  }, [register, routeParams.newServiceCategoryId, setValue]);

  // newCategoryId is sent back from categories screen, when picking a new category
  useEffect(() => {
    if (!routeParams.newCategoryId) return;
    setValue('categoryPrimaryId', routeParams.newCategoryId);
    register('categoryPrimaryId');
  }, [register, routeParams.newCategoryId, setValue]);

  // newAddress is sent back from my-address screen, when modifying the address
  useEffect(() => {
    if (!routeParams.newAddress) return;
    setValue('address', routeParams.newAddress);
    register('address');
  }, [register, routeParams.newAddress, setValue]);

  const useNeedFoundModal = isRequest && (listing.status === 'available' || listing.status === 'expired');

  const startNeedFoundModal = useCallback(() => {
    setNeedFoundModalCurrentStep(NeedFoundModalSteps.FoundOrDelete);
    setNeedFoundModalNextStep(NeedFoundModalSteps.FoundOrDelete);
    setIsNeedFoundModalOpen(true);
  }, []);

  const removeListing = useCallback(() => {
    if (useNeedFoundModal) {
      startNeedFoundModal();
      return;
    }
    setShowActions(true);
  }, [setShowActions, startNeedFoundModal, useNeedFoundModal]);

  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = { save: saveChanges, remove: removeListing };
    return () => {
      actionsRef.current = null;
    };
  }, [actionsRef, removeListing, saveChanges]);

  const stepChanged = (step: NeedFoundModalSteps) => {
    if (step === NeedFoundModalSteps.FoundElsewhere) {
      updateListing.mutate({ status: 'loaned' });
    }
    if (step === NeedFoundModalSteps.Done) {
      // Create a new loan request for the specified listing.id, selectedLender, selectedStartDate & selectedEndDate
      createLoan({
        listingId: listing.id,
        otherUserId: selectedLender,
        start: selectedStartDate!,
        end: selectedEndDate!,
      });
      // And manually mark as found the listing
      updateListing.mutate({ status: 'loaned' });
    }
    setNeedFoundModalCurrentStep(step);
  };

  const closeScreen = () => {
    navigation.goBack();
  };

  const goToBillboard = () => {
    closeScreen();
    navigation.navigate('billboard', { screen: 'all-listings' });
  };

  const description = form.watch('description');

  return (
    <FormProvider {...form}>
      <EditListingResourceHeader
        isHauteGaspesieOffer={isHauteGaspesieOffer}
        isExpirable={isExpirable}
        t={t}
        setResourceType={setResourceType}
        perishableEnabled={perishableEnabled}
        showRequestTransactionMode={showRequestTransactionMode}
        setTransactionMode={setTransactionMode}
        requestTransactionOptions={requestTransactionOptions}
        transactionMode={transactionMode}
        listing={listing}
        onImageChange={onImageChange}
        onSharePress={onSharePress}
      />

      <EditListingInformation
        control={control}
        listing={listing}
        t={t}
        isHauteGaspesieOffer={isHauteGaspesieOffer}
        isService={isService}
        description={description}
        isRequest={isRequest}
        user={user}
        onChangeAutomaticMessage={onChangeAutomaticMessage}
        assistantMessageDefault={assistantMessageDefault}
        setTransactionMode={setTransactionMode}
        transactionOptions={transactionOptions}
        transactionMode={transactionMode}
        loanFeeEnabled={loanFeeEnabled}
        loanFeeModalsState={loanFeeModalsState}
        setLoanFeeEnabled={setLoanFeeEnabled}
        isExpirable={isExpirable}
        form={form}
        isOffer={isOffer}
        isPublicServiceRequest={isPublicServiceRequest}
        loanFeeAvailable={loanFeeAvailable}
      />
      <EditListingAvailability
        isRequest={isRequest}
        isService={isService}
        form={form}
        listing={listing}
        isHauteGaspesieOffer={isHauteGaspesieOffer}
        user={user}
        isOffer={isOffer}
        isExpirable={isExpirable}
        isFormInvalid={isFormInvalid}
        t={t}
      />

      <NeedFoundModal
        setIsNeedFoundModalOpen={setIsNeedFoundModalOpen}
        isNeedFoundModalOpen={isNeedFoundModalOpen}
        t={t}
        needFoundModalCurrentStep={needFoundModalCurrentStep}
        stepChanged={stepChanged}
        setShowActions={setShowActions}
        needFoundModalNextStep={needFoundModalNextStep}
        setNeedFoundModalNextStep={setNeedFoundModalNextStep}
        goToBillboard={goToBillboard}
        closeScreen={closeScreen}
        deviceHeight={deviceHeight}
        listingUsersQuery={listingUsersQuery}
        selectedLender={selectedLender}
        setSelectedLender={setSelectedLender}
        onDatePress={onDatePress}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
      />

      <LoanFeeModals state={loanFeeModalsState} />
    </FormProvider>
  );
};

function NeedFoundModal({
  setIsNeedFoundModalOpen,
  isNeedFoundModalOpen,
  t,
  needFoundModalCurrentStep,
  stepChanged,
  setShowActions,
  needFoundModalNextStep,
  setNeedFoundModalNextStep,
  goToBillboard,
  closeScreen,
  deviceHeight,
  listingUsersQuery,
  selectedLender,
  setSelectedLender,
  onDatePress,
  selectedStartDate,
  selectedEndDate,
}: {
  setIsNeedFoundModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isNeedFoundModalOpen: boolean;
  t: React.ContextType<typeof ListingContext>['t'];
  needFoundModalCurrentStep: NeedFoundModalSteps;
  stepChanged: (step: NeedFoundModalSteps) => void;
  setShowActions: React.Dispatch<React.SetStateAction<boolean>>;
  needFoundModalNextStep: NeedFoundModalSteps;
  setNeedFoundModalNextStep: React.Dispatch<React.SetStateAction<NeedFoundModalSteps>>;
  goToBillboard: () => void;
  closeScreen: () => void;
  deviceHeight: number;
  listingUsersQuery: ReturnType<typeof useOneListingPossibleLenders>;
  selectedLender: string;
  setSelectedLender: React.Dispatch<React.SetStateAction<string>>;
  onDatePress: (date: Date) => void;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
}) {
  return (
    <Modal onClose={() => setIsNeedFoundModalOpen(false)} open={isNeedFoundModalOpen} transparent>
      <Modal.Card>
        <View style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Pressable onPress={() => setIsNeedFoundModalOpen(false)}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} />
          </Pressable>
        </View>

        <View style={{ marginTop: 10, marginHorizontal: 24 }}>
          <Modal.Title>{t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.title`)}</Modal.Title>
        </View>

        {needFoundModalCurrentStep === NeedFoundModalSteps.FoundOrDelete && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x4" marginBottom="spacing.general.x8">
              <Text fontSize={16} textAlign="center">
                {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.body`)}
              </Text>
            </Column>

            <Button
              marginBottom="spacing.general.x4"
              onPress={() => stepChanged(NeedFoundModalSteps.FoundPCOrElsewhere)}
              variant="primary"
            >
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>

            <Button
              marginBottom="spacing.general.x4"
              onPress={() => {
                setIsNeedFoundModalOpen(false);
                setShowActions(true);
              }}
              variant="secondary"
            >
              {`${t('one-listing:deleteButtonText')} ...`}
            </Button>
          </Box>
        )}

        {needFoundModalCurrentStep === NeedFoundModalSteps.FoundPCOrElsewhere && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x4" marginBottom="spacing.general.x8">
              <Row alignItems="center">
                <Radio
                  checked={needFoundModalNextStep === NeedFoundModalSteps.SelectLender}
                  onPress={() => setNeedFoundModalNextStep(NeedFoundModalSteps.SelectLender)}
                />
                <Pressable
                  onPress={() => setNeedFoundModalNextStep(NeedFoundModalSteps.SelectLender)}
                  style={{ flexShrink: 1 }}
                >
                  <Text fontSize={16} marginStart="spacing.general.x2">
                    {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.gotoSelectLender`)}
                  </Text>
                </Pressable>
              </Row>

              <Row alignItems="center">
                <Radio
                  checked={needFoundModalNextStep === NeedFoundModalSteps.FoundElsewhere}
                  onPress={() => setNeedFoundModalNextStep(NeedFoundModalSteps.FoundElsewhere)}
                />
                <Pressable
                  onPress={() => setNeedFoundModalNextStep(NeedFoundModalSteps.FoundElsewhere)}
                  style={{ flexShrink: 1 }}
                >
                  <Text fontSize={16} marginStart="spacing.general.x2">
                    {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.gotoFoundElsewhere`)}
                  </Text>
                </Pressable>
              </Row>
            </Column>

            <Button
              disabled={needFoundModalNextStep <= NeedFoundModalSteps.FoundPCOrElsewhere}
              marginBottom="spacing.general.x4"
              onPress={() => stepChanged(needFoundModalNextStep)}
              variant="secondary"
            >
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>
          </Box>
        )}

        {needFoundModalCurrentStep === NeedFoundModalSteps.FoundElsewhere && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x4" marginBottom="spacing.general.x8">
              <Text fontSize={16} textAlign="center">
                {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.body`)}
              </Text>
            </Column>

            <Button marginBottom="spacing.general.x4" onPress={goToBillboard} variant="primary">
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>

            <Button marginBottom="spacing.general.x4" onPress={closeScreen} variant="secondary">
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.secondaryButtonText`)}
            </Button>
          </Box>
        )}

        {needFoundModalCurrentStep === NeedFoundModalSteps.SelectLender && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x8" marginBottom="spacing.general.x8">
              <Text fontSize={16} textAlign="center">
                {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.body`)}
              </Text>

              <View
                style={{
                  maxHeight: deviceHeight * 0.25,
                  paddingHorizontal: theme.spacing['spacing.general.x4'],
                }}
              >
                <ScrollView>
                  {listingUsersQuery.data?.map((u) => (
                    <Row alignItems="center" key={u.id} marginBottom="spacing.general.x2">
                      <Radio checked={selectedLender === u.id} onPress={() => setSelectedLender(u.id)} />
                      <Pressable onPress={() => setSelectedLender(u.id)} style={{ flexShrink: 1 }}>
                        <Row alignItems="center" gap="spacing.general.x2" marginStart="spacing.general.x4">
                          <PCImage
                            source={{ uri: u.profile.picture }}
                            style={{ width: 32, height: 32, borderRadius: 16 }}
                          />
                          <Text fontSize={16} fontWeight="600">
                            {u.profile.firstName} {u.profile.lastName}
                          </Text>
                        </Row>
                      </Pressable>
                    </Row>
                  ))}
                </ScrollView>
              </View>
            </Column>

            <Button
              disabled={!selectedLender}
              marginBottom="spacing.general.x4"
              onPress={() => stepChanged(NeedFoundModalSteps.SelectDates)}
              variant="secondary"
            >
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>
          </Box>
        )}

        {needFoundModalCurrentStep === NeedFoundModalSteps.SelectDates && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x8" marginBottom="spacing.general.x8">
              <Text fontSize={16} textAlign="center">
                {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.body`)}
              </Text>

              <Calendar
                {...{ onDatePress }}
                initial={selectedStartDate}
                selected={[selectedStartDate, selectedEndDate].filter((d): d is Date => isDate(d))}
              />
            </Column>

            <Button
              disabled={!selectedStartDate || !selectedEndDate}
              marginBottom="spacing.general.x4"
              onPress={() => stepChanged(NeedFoundModalSteps.Done)}
              variant="primary"
            >
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>
          </Box>
        )}

        {needFoundModalCurrentStep === NeedFoundModalSteps.Done && (
          <Box paddingHorizontal="spacing.general.x4">
            <Column gap="spacing.general.x4" marginBottom="spacing.general.x8">
              <Text fontSize={16} textAlign="center">
                {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.body`)}
              </Text>
            </Column>

            <Button marginBottom="spacing.general.x4" onPress={goToBillboard} variant="primary">
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.primaryButtonText`)}
            </Button>

            <Button marginBottom="spacing.general.x4" onPress={closeScreen} variant="secondary">
              {t(`one-listing:needFoundModal.step${needFoundModalCurrentStep.toString()}.secondaryButtonText`)}
            </Button>
          </Box>
        )}
      </Modal.Card>
    </Modal>
  );
}

function EditListingAvailability({
  isRequest,
  isService,
  form,
  listing,
  isHauteGaspesieOffer,
  user,
  isOffer,
  isExpirable,
  isFormInvalid,
  t,
}: {
  isRequest: boolean;
  isService: boolean;
  form: React.ContextType<typeof ListingContext>['form'];
  listing: Listing;
  isHauteGaspesieOffer: boolean;
  user: ReturnType<typeof useUser>['data'];
  isOffer: boolean;
  isExpirable: boolean;
  isFormInvalid: boolean;
  t: React.ContextType<typeof ListingContext>['t'];
}) {
  return (
    <Column gap="spacing.general.x5" marginTop="spacing.general.x5">
      {isRequest && !isService ? <TravelDistanceSection defaultToPreference={false} form={form} /> : null}

      {isRequest ? <DateSection initial={listing.start ? new Date(listing.start) : new Date()} /> : null}

      {!isHauteGaspesieOffer &&
      isFeatureEnabled(FeatureFlag.listingPromptAvailability, user?.featureFlags) &&
      isOffer &&
      !isExpirable ? (
        <AvailabilitySection form={form} id={listing.id} />
      ) : undefined}

      {isRequest && isService ? (
        <PublicServiceRequestSection listingId={listing.id} numParticipants={listing.numParticipants} />
      ) : undefined}

      <GroupsSection alwaysShow includeAllSectorGroups={isHauteGaspesieOffer} />

      {isHauteGaspesieOffer ? <DuplicatesStepper form={form} /> : null}

      <ViewsSection numFavourites={isRequest ? undefined : listing.numFavourites} numViews={listing.numViews} />

      {isFormInvalid ? <RemoteError message={t('one-listing:invalidFormError')} /> : null}
    </Column>
  );
}

function EditListingInformation({
  control,
  listing,
  t,
  isHauteGaspesieOffer,
  isService,
  description,
  isRequest,
  user,
  onChangeAutomaticMessage,
  assistantMessageDefault,
  setTransactionMode,
  transactionOptions,
  transactionMode,
  loanFeeEnabled,
  loanFeeModalsState,
  setLoanFeeEnabled,
  isExpirable,
  form,
  isOffer,
  isPublicServiceRequest,
  loanFeeAvailable,
}: {
  control: React.ContextType<typeof ListingContext>['form']['control'];
  listing: Listing;
  t: React.ContextType<typeof ListingContext>['t'];
  isHauteGaspesieOffer: boolean;
  isService: boolean;
  description: string | null | undefined;
  isRequest: boolean;
  user: ReturnType<typeof useUser>['data'];
  onChangeAutomaticMessage: (text: string) => void;
  assistantMessageDefault: string;
  setTransactionMode: (mode: TransactionMode) => void;
  transactionOptions: { label: string; value: TransactionMode }[];
  transactionMode: TransactionMode | null | undefined;
  loanFeeEnabled: boolean;
  loanFeeModalsState: LoanFeeModalsState;
  setLoanFeeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isExpirable: boolean;
  form: React.ContextType<typeof ListingContext>['form'];
  isOffer: boolean;
  isPublicServiceRequest: boolean;
  loanFeeAvailable: boolean;
}) {
  return (
    <Column flex={1} gap="spacing.general.x4" marginTop="spacing.general.x4">
      <ControlledInput
        {...{ control }}
        editable={!!listing}
        label={t(
          isHauteGaspesieOffer
            ? 'hauteGaspesie.resourceName'
            : isService
              ? 'itemInformationSection.serviceNameFieldLabel'
              : 'itemInformationSection.nameFieldLabel',
          { ns: 'new' },
        )}
        markAsRequired
        maxLength={64}
        name="item.name"
        placeholder={t(
          isHauteGaspesieOffer
            ? 'hauteGaspesie.resourceNamePlaceholder'
            : isService
              ? 'itemInformationSection.serviceNameFieldPlaceholder'
              : 'itemInformationSection.nameFieldPlaceholder',
          { ns: 'new' },
        )}
      />
      <EditListingDescription
        description={description}
        control={control}
        listing={listing}
        t={t}
        isHauteGaspesieOffer={isHauteGaspesieOffer}
        isService={isService}
      />
      {isRequest && isFeatureEnabled(FeatureFlag.conciergerie, user?.featureFlags) ? (
        <ControlledInput
          {...{ control }}
          label={t('automaticMessageCard.automaticMessageLabel', { ns: 'one-listing' })}
          markAsRequired
          multiline
          name="automaticMessage"
          onChangeText={(text) => onChangeAutomaticMessage(text ?? '')}
          placeholder={assistantMessageDefault}
          scrollEnabled={false}
          style={{ minHeight: 92, maxHeight: 150 }}
        />
      ) : null}
      <EditListingTransactionTerms
        isHauteGaspesieOffer={isHauteGaspesieOffer}
        setTransactionMode={setTransactionMode}
        transactionOptions={transactionOptions}
        transactionMode={transactionMode}
        control={control}
        t={t}
        loanFeeEnabled={loanFeeEnabled}
        loanFeeModalsState={loanFeeModalsState}
        setLoanFeeEnabled={setLoanFeeEnabled}
        isExpirable={isExpirable}
        form={form}
        listing={listing}
      />
      {!isHauteGaspesieOffer &&
      isOffer &&
      isFeatureEnabled(FeatureFlag.listingPromptEstimatedPrice, user?.featureFlags) &&
      !isPublicServiceRequest ? (
        <ControlledInput
          {...{ control }}
          keyboardType="number-pad"
          label={t('itemInformationSection.valueFieldLabel', { ns: 'new' })}
          left={<Text variant="bodyBase">$</Text>}
          name="estimatedPrice"
          nullable
          placeholder={t('itemInformationSection.valueFieldPlaceholder', { ns: 'new' })}
        />
      ) : null}
      {loanFeeAvailable && isOffer && !isHauteGaspesieOffer ? (
        <LoanFeeFormFields {...{ loanFeeEnabled, setLoanFeeEnabled, modalsState: loanFeeModalsState }} />
      ) : null}
      {isFeatureEnabled(FeatureFlag.popularItemsByCats, user?.featureFlags) ? (
        <CategorySection goBackTo="edit-listing" listingId={listing.id} />
      ) : null}
    </Column>
  );
}

function EditListingTransactionTerms({
  isHauteGaspesieOffer,
  setTransactionMode,
  transactionOptions,
  transactionMode,
  control,
  t,
  loanFeeEnabled,
  loanFeeModalsState,
  setLoanFeeEnabled,
  isExpirable,
  form,
  listing,
}: {
  isHauteGaspesieOffer: boolean;
  setTransactionMode: (mode: TransactionMode) => void;
  transactionOptions: { label: string; value: TransactionMode }[];
  transactionMode: TransactionMode | null | undefined;
  control: React.ContextType<typeof ListingContext>['form']['control'];
  t: React.ContextType<typeof ListingContext>['t'];
  loanFeeEnabled: boolean;
  loanFeeModalsState: LoanFeeModalsState;
  setLoanFeeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isExpirable: boolean;
  form: React.ContextType<typeof ListingContext>['form'];
  listing: Listing;
}): React.ReactNode {
  return isHauteGaspesieOffer ? (
    <Column
      backgroundColor="surface.tertiary-neutral"
      borderRadius="radius.xl"
      gap="spacing.general.x4"
      padding="spacing.general.x4"
    >
      <TransactionModeToggle onChange={setTransactionMode} options={transactionOptions} value={transactionMode} />
      {transactionMode === TransactionMode.sell ? (
        <ControlledInput
          backgroundColor={theme.colors['surface.default']}
          control={control}
          label={t('hauteGaspesie.price', { ns: 'new' })}
          labelSuffix={`(${t('mandatory', { ns: 'common' })})*`}
          name="price"
          placeholder={t('hauteGaspesie.pricePlaceholder', { ns: 'new' })}
        />
      ) : null}

      {transactionMode === TransactionMode.lend ? (
        <LoanFeeFormFields
          loanFeeEnabled={loanFeeEnabled}
          modalsState={loanFeeModalsState}
          setLoanFeeEnabled={setLoanFeeEnabled}
        />
      ) : null}

      {isExpirable ? <ExpirableAvailabilitySection /> : null}

      {isExpirable ? <InventoryField form={form} /> : null}

      {isExpirable ? <UrgentSection /> : null}

      {!isExpirable ? <AvailabilitySection form={form} id={listing.id} useInventory /> : null}
    </Column>
  ) : null;
}

function EditListingDescription({
  description,
  control,
  listing,
  t,
  isHauteGaspesieOffer,
  isService,
}: {
  description: string | null | undefined;
  control: React.ContextType<typeof ListingContext>['form']['control'];
  listing: Listing;
  t: React.ContextType<typeof ListingContext>['t'];
  isHauteGaspesieOffer: boolean;
  isService: boolean;
}) {
  return (
    <Row>
      {!description || description === '' ? <BonificationBadge /> : null}
      <ControlledInput
        {...{ control }}
        editable={!!listing}
        label={t('itemInformationSection.descriptionFieldLabel', { ns: 'new' })}
        labelSuffix={listing.type === ListingType.offer ? `(${t('suggestede', { ns: 'common' })})` : null}
        maxLength={isHauteGaspesieOffer ? 280 : undefined}
        multiline
        name="description"
        markAsRequired={listing.type === ListingType.request}
        nullable={listing.type === ListingType.offer}
        placeholder={t(
          isHauteGaspesieOffer
            ? 'hauteGaspesie.descriptionPlaceholder'
            : isService
              ? 'itemInformationSection.serviceDescriptionFieldPlaceholder'
              : `itemInformationSection.descriptionFieldPlaceholder.${listing.type}`,
          { ns: 'new' },
        )}
        scrollEnabled={false} // Required for multiline input to scroll into view, when given focus and the keyboard is shown
        style={{ minHeight: 60, maxHeight: 150 }}
      />
    </Row>
  );
}

function EditListingResourceHeader({
  isHauteGaspesieOffer,
  isExpirable,
  t,
  setResourceType,
  perishableEnabled,
  showRequestTransactionMode,
  setTransactionMode,
  requestTransactionOptions,
  transactionMode,
  listing,
  onImageChange,
  onSharePress,
}: {
  isHauteGaspesieOffer: boolean;
  isExpirable: boolean;
  t: React.ContextType<typeof ListingContext>['t'];
  setResourceType: (nextSubtype: ListingSubtype.object | ListingSubtype.expirable) => void;
  perishableEnabled: boolean;
  showRequestTransactionMode: boolean;
  setTransactionMode: (mode: TransactionMode) => void;
  requestTransactionOptions: { label: string; value: TransactionMode }[];
  transactionMode: TransactionMode | null | undefined;
  listing: Listing;
  onImageChange: React.ContextType<typeof ListingContext>['onImageChange'];
  onSharePress: () => Promise<void>;
}) {
  return (
    <Column gap="spacing.general.x4">
      {isHauteGaspesieOffer ? (
        <Row
          backgroundColor="surface.primary-neutral"
          borderRadius="radius.xl"
          gap="spacing.general.x1"
          padding="spacing.general.x1"
        >
          <ResourceTypeButton
            icon={
              <IconTools
                strokeWidth={ICON_STROKE_WIDTH}
                size={22}
                color={!isExpirable ? theme.colors['text.inverse.primary'] : theme.colors['text.primary']}
              />
            }
            label={t('hauteGaspesie.resourceType.equipment', { ns: 'new' })}
            onPress={() => setResourceType(ListingSubtype.object)}
            selected={!isExpirable}
          />
          {perishableEnabled || isExpirable ? (
            <ResourceTypeButton
              icon={
                <Perishable
                  strokeWidth={ICON_STROKE_WIDTH}
                  width={40}
                  height={40}
                  color={isExpirable ? theme.colors['text.inverse.primary'] : theme.colors['text.primary']}
                />
              }
              label={t('hauteGaspesie.resourceType.perishable', { ns: 'new' })}
              onPress={() => setResourceType(ListingSubtype.expirable)}
              selected={isExpirable}
            />
          ) : null}
        </Row>
      ) : null}
      {showRequestTransactionMode ? (
        <TransactionModeToggle
          onChange={setTransactionMode}
          options={requestTransactionOptions}
          value={transactionMode}
          wrapOnSmallUI={false}
        />
      ) : null}
      <Row>
        {isQuickOffer({ type: listing.type, picture: listing.pictures?.[0]?.url ?? listing.picture }) ? (
          <BonificationBadge />
        ) : null}
        <ListingHero {...listing} hideCorpoBadge onChangePictures={onImageChange} onSharePress={onSharePress} />
      </Row>
    </Column>
  );
}

function useEditListingLoanFee(listing: Listing) {
  const [loanFeeEnabled, setLoanFeeEnabled] = useState<boolean>(
    Boolean(listing.loanFee || listing.loanFeeDescription || listing.loanFeePaymentMethod),
  );
  const loanFeeModalsState = useLoanFeeModals();
  return { loanFeeEnabled, setLoanFeeEnabled, loanFeeModalsState };
}

function useEditListingTransactionMode({
  isRequest,
  isExpirable,
  isHauteGaspesieOffer,
  showRequestTransactionMode,
  transactionMode,
  setLoanFeeEnabled,
  setValue,
  t,
}: {
  isRequest: boolean;
  isExpirable: boolean;
  isHauteGaspesieOffer: boolean;
  showRequestTransactionMode: boolean;
  transactionMode: Listing['transactionMode'];
  setLoanFeeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setValue: React.ContextType<typeof ListingContext>['form']['setValue'];
  t: React.ContextType<typeof ListingContext>['t'];
}) {
  const synchronizeTransactionMode = useEffectEvent(() => {
    if (showRequestTransactionMode && (!transactionMode || (isExpirable && transactionMode === TransactionMode.lend))) {
      setValue('transactionMode', isExpirable ? TransactionMode.sell : TransactionMode.lend, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    if (!isHauteGaspesieOffer) return;
    if (!transactionMode || (isExpirable && transactionMode === TransactionMode.lend)) {
      setValue('transactionMode', TransactionMode.sell, { shouldDirty: true, shouldValidate: true });
      if (transactionMode === TransactionMode.lend) {
        setLoanFeeEnabled(false);
        setValue('loanFee', null, { shouldDirty: true });
        setValue('loanFeeDescription', null, { shouldDirty: true });
        setValue('loanFeePaymentMethod', null, { shouldDirty: true });
      }
    }
  });
  useEffect(() => {
    const timeout = setTimeout(synchronizeTransactionMode, 0);
    return () => clearTimeout(timeout);
  }, [isExpirable, isHauteGaspesieOffer, showRequestTransactionMode, transactionMode]);

  const setResourceType = (nextSubtype: ListingSubtype.object | ListingSubtype.expirable) => {
    setValue('subtype', nextSubtype, { shouldDirty: true, shouldValidate: true });
    if (nextSubtype === ListingSubtype.expirable && transactionMode === TransactionMode.lend) {
      setValue('transactionMode', TransactionMode.sell, { shouldDirty: true, shouldValidate: true });
      setLoanFeeEnabled(false);
      setValue('loanFee', null, { shouldDirty: true });
      setValue('loanFeeDescription', null, { shouldDirty: true });
      setValue('loanFeePaymentMethod', null, { shouldDirty: true });
    }
    if (nextSubtype === ListingSubtype.object) {
      setValue('start', null, { shouldDirty: true, shouldValidate: true });
      setValue('end', null, { shouldDirty: true, shouldValidate: true });
      setValue('urgent', false, { shouldDirty: true, shouldValidate: true });
    }
  };

  const setTransactionMode = (mode: TransactionMode) => {
    setValue('transactionMode', mode, { shouldDirty: true, shouldValidate: true });
    if (isRequest) {
      setValue('price', null, { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (mode !== TransactionMode.sell) {
      setValue('price', null, { shouldDirty: true, shouldValidate: true });
    }
    if (mode !== TransactionMode.lend) {
      setLoanFeeEnabled(false);
      setValue('loanFee', null, { shouldDirty: true });
      setValue('loanFeeDescription', null, { shouldDirty: true });
      setValue('loanFeePaymentMethod', null, { shouldDirty: true });
    }
  };

  const transactionOptions = [
    { label: t('hauteGaspesie.transaction.sell', { ns: 'new' }), value: TransactionMode.sell },
    ...(!isExpirable
      ? [{ label: t('hauteGaspesie.transaction.lend', { ns: 'new' }), value: TransactionMode.lend }]
      : []),
    { label: t('hauteGaspesie.transaction.give', { ns: 'new' }), value: TransactionMode.give },
  ];

  const requestTransactionOptions = [
    { label: t('itemInformationSection.requestTransactionMode.sell', { ns: 'new' }), value: TransactionMode.sell },
    ...(!isExpirable
      ? [
          {
            label: t('itemInformationSection.requestTransactionMode.lend', { ns: 'new' }),
            value: TransactionMode.lend,
          },
        ]
      : []),
    { label: t('itemInformationSection.requestTransactionMode.give', { ns: 'new' }), value: TransactionMode.give },
  ];

  return { setResourceType, setTransactionMode, transactionOptions, requestTransactionOptions };
}
